<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minValue, maxValue, integer } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useCommunicationErrorMessage } from 'dashboard/composables/useCommunicationErrorMessage';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { friendlyMessage } = useCommunicationErrorMessage();

const record = computed(
  () => getters['communicationConnection/getCommunicationConnection'].value
);
const uiFlags = computed(
  () => getters['communicationConnection/getUIFlags'].value
);
const testResult = computed(
  () => getters['communicationConnection/getTestResult'].value
);
const summary = computed(
  () => getters['communicationConnection/getSummary'].value
);

const isNew = computed(() => !record.value?.id);
const deleteDialogRef = ref(null);

const form = reactive({
  url: '',
  apiKey: '',
  timeout: 10,
  sslVerify: true,
  enabled: true,
});

const populateForm = () => {
  form.url = record.value?.url || '';
  form.apiKey = '';
  form.timeout = record.value?.timeout ?? 10;
  form.sslVerify = record.value?.ssl_verify ?? true;
  form.enabled = record.value?.enabled ?? true;
};

const rules = {
  url: { required },
  timeout: {
    required,
    integer,
    minValue: minValue(1),
    maxValue: maxValue(120),
  },
  apiKey: {
    required: value => !isNew.value || !!value,
  },
};

const v$ = useVuelidate(rules, form);

const fetchSummarySafely = async () => {
  if (!record.value?.id) return;
  try {
    await store.dispatch('communicationConnection/fetchSummary');
  } catch (error) {
    // Polling failures shouldn't interrupt the page with a toast every 60s —
    // the Gateway Status card itself shows "Unhealthy" when summary is stale/missing.
  }
};

const { startRefetching } = useLiveRefresh(fetchSummarySafely, 60000);

const fetchConnection = async () => {
  await store.dispatch('communicationConnection/get');
  populateForm();
  await fetchSummarySafely();
  startRefetching();
};

onMounted(fetchConnection);

const buildPayload = () => {
  const payload = {
    url: form.url,
    timeout: form.timeout,
    ssl_verify: form.sslVerify,
    enabled: form.enabled,
    provider: 'evolution_gateway',
  };
  if (form.apiKey) {
    payload.api_key = form.apiKey;
  }
  return payload;
};

const saveConnection = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    if (isNew.value) {
      await store.dispatch('communicationConnection/create', buildPayload());
    } else {
      await store.dispatch('communicationConnection/update', buildPayload());
    }
    useAlert(t('COMMUNICATION_CONNECTION.FORM.SAVE_SUCCESS'));
    populateForm();
    await fetchSummarySafely();
  } catch (error) {
    useAlert(friendlyMessage(error));
  }
};

const runTestConnection = async () => {
  const result = await store.dispatch('communicationConnection/testConnection');
  if (result?.reachable && result?.authenticated) {
    useAlert(t('COMMUNICATION_CONNECTION.TEST_CONNECTION.SUCCESS'));
  } else {
    useAlert(
      result?.error || t('COMMUNICATION_CONNECTION.TEST_CONNECTION.FAILURE')
    );
  }
  await fetchSummarySafely();
};

const openDeleteDialog = () => deleteDialogRef.value?.open();

const removeConnection = async () => {
  try {
    await store.dispatch('communicationConnection/delete');
    useAlert(t('COMMUNICATION_CONNECTION.DELETE.SUCCESS'));
    deleteDialogRef.value?.close();
    populateForm();
  } catch (error) {
    useAlert(friendlyMessage(error));
  }
};

const isHealthy = computed(() => summary.value?.gateway_reachable === true);
const connectionStatusLabel = computed(() => {
  if (isNew.value)
    return t('COMMUNICATION_CONNECTION.CONNECTION.STATUS_NOT_CONFIGURED');
  return isHealthy.value
    ? t('COMMUNICATION_CONNECTION.CONNECTION.STATUS_CONNECTED')
    : t('COMMUNICATION_CONNECTION.CONNECTION.STATUS_DISCONNECTED');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('COMMUNICATION_CONNECTION.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('COMMUNICATION_CONNECTION.HEADER')"
        :description="t('COMMUNICATION_CONNECTION.DESCRIPTION')"
      />
      <div class="flex gap-4 mt-2 text-sm">
        <router-link
          :to="{ name: 'communication_connection_index' }"
          class="text-n-blue-11 font-medium"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.SETTINGS') }}
        </router-link>
        <router-link
          :to="{ name: 'communication_whatsapp_accounts' }"
          class="text-n-blue-11 hover:underline"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.WHATSAPP_ACCOUNTS') }}
        </router-link>
        <router-link
          :to="{ name: 'communication_inbox_mapping' }"
          class="text-n-blue-11 hover:underline"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.INBOX_MAPPING') }}
        </router-link>
      </div>
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-3xl">
        <!-- Gateway Status -->
        <CardLayout v-if="!isNew">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.TITLE') }}
          </h3>
          <div class="flex flex-wrap gap-x-8 gap-y-3 mt-3 text-sm">
            <div>
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  isHealthy
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
              >
                {{
                  isHealthy
                    ? t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.HEALTHY')
                    : t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.UNHEALTHY')
                }}
              </span>
            </div>
            <div>
              <p class="text-n-slate-10 mb-0">
                {{ t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.VERSION') }}
              </p>
              <p class="text-n-slate-12 mb-0 font-medium">
                {{
                  summary?.gateway_version ||
                  t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.UNKNOWN')
                }}
              </p>
            </div>
            <div>
              <p class="text-n-slate-10 mb-0">
                {{ t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.RESPONSE_TIME') }}
              </p>
              <p class="text-n-slate-12 mb-0 font-medium">
                {{
                  summary?.gateway_latency_ms !== undefined &&
                  summary?.gateway_latency_ms !== null
                    ? t(
                        'COMMUNICATION_CONNECTION.GATEWAY_STATUS.RESPONSE_TIME_MS',
                        { latency: summary.gateway_latency_ms }
                      )
                    : t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.UNKNOWN')
                }}
              </p>
            </div>
            <div>
              <p class="text-n-slate-10 mb-0">
                {{ t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.LAST_CHECK') }}
              </p>
              <p class="text-n-slate-12 mb-0 font-medium">
                {{
                  summary?.last_health_check
                    ? messageTimestamp(summary.last_health_check)
                    : t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.UNKNOWN')
                }}
              </p>
            </div>
          </div>
        </CardLayout>

        <!-- Statistics -->
        <CardLayout v-if="!isNew">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('COMMUNICATION_CONNECTION.STATISTICS.TITLE') }}
          </h3>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
            <div>
              <p class="text-n-slate-10 mb-0 text-sm">
                {{
                  t('COMMUNICATION_CONNECTION.STATISTICS.CONNECTED_ACCOUNTS')
                }}
              </p>
              <p class="text-n-slate-12 mb-0 text-2xl font-semibold">
                {{ summary?.connected_accounts ?? '—' }}
              </p>
            </div>
            <div>
              <p class="text-n-slate-10 mb-0 text-sm">
                {{ t('COMMUNICATION_CONNECTION.STATISTICS.ACTIVE_INBOXES') }}
              </p>
              <p class="text-n-slate-12 mb-0 text-2xl font-semibold">
                {{ summary?.active_inboxes ?? '—' }}
              </p>
            </div>
            <div>
              <p class="text-n-slate-10 mb-0 text-sm">
                {{
                  t('COMMUNICATION_CONNECTION.STATISTICS.TOTAL_CONVERSATIONS')
                }}
              </p>
              <p class="text-n-slate-12 mb-0 text-2xl font-semibold">
                {{ summary?.total_conversations ?? '—' }}
              </p>
            </div>
            <div>
              <p class="text-n-slate-10 mb-0 text-sm">
                {{ t('COMMUNICATION_CONNECTION.STATISTICS.MESSAGES_TODAY') }}
              </p>
              <p class="text-n-slate-12 mb-0 text-2xl font-semibold">
                {{ summary?.messages_today ?? '—' }}
              </p>
            </div>
          </div>
        </CardLayout>

        <!-- Communication Connection -->
        <CardLayout>
          <div class="flex items-center justify-between">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('COMMUNICATION_CONNECTION.CONNECTION.TITLE') }}
            </h3>
            <span
              v-if="!isNew"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="
                isHealthy
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{ connectionStatusLabel }}
            </span>
          </div>

          <form
            class="flex flex-col gap-4 mt-4"
            @submit.prevent="saveConnection"
          >
            <Input
              v-model="form.url"
              name="communication_connection_url"
              autocomplete="off"
              :label="t('COMMUNICATION_CONNECTION.FORM.URL.LABEL')"
              :placeholder="t('COMMUNICATION_CONNECTION.FORM.URL.PLACEHOLDER')"
              :message="
                v$.url.$error
                  ? t('COMMUNICATION_CONNECTION.FORM.URL.ERROR')
                  : ''
              "
              :message-type="v$.url.$error ? 'error' : 'info'"
              @blur="v$.url.$touch"
            />

            <div>
              <Input
                v-model="form.apiKey"
                type="password"
                name="communication_connection_api_key"
                autocomplete="new-password"
                :label="t('COMMUNICATION_CONNECTION.FORM.API_KEY.LABEL')"
                :placeholder="
                  t('COMMUNICATION_CONNECTION.FORM.API_KEY.PLACEHOLDER')
                "
                :message="
                  v$.apiKey.$error
                    ? t('COMMUNICATION_CONNECTION.FORM.API_KEY.ERROR')
                    : ''
                "
                :message-type="v$.apiKey.$error ? 'error' : 'info'"
                @blur="v$.apiKey.$touch"
              />
              <p class="text-xs text-n-slate-10 mt-1">
                {{
                  record?.api_key_present
                    ? t('COMMUNICATION_CONNECTION.CONNECTION.API_KEY_MASKED')
                    : t('COMMUNICATION_CONNECTION.CONNECTION.API_KEY_NOT_SET')
                }}
              </p>
            </div>

            <Input
              v-model="form.timeout"
              type="number"
              min="1"
              max="120"
              :label="t('COMMUNICATION_CONNECTION.FORM.TIMEOUT.LABEL')"
              :message="
                v$.timeout.$error
                  ? t('COMMUNICATION_CONNECTION.FORM.TIMEOUT.ERROR')
                  : ''
              "
              :message-type="v$.timeout.$error ? 'error' : 'info'"
              @blur="v$.timeout.$touch"
            />

            <div class="flex items-center gap-2">
              <Switch v-model="form.sslVerify" />
              <span class="text-sm text-n-slate-12">
                {{ t('COMMUNICATION_CONNECTION.FORM.SSL_VERIFY.LABEL') }}
              </span>
            </div>

            <div class="flex items-center gap-2">
              <Switch v-model="form.enabled" />
              <span class="text-sm text-n-slate-12">
                {{ t('COMMUNICATION_CONNECTION.FORM.ENABLED.LABEL') }}
              </span>
            </div>

            <div class="flex items-center gap-2 mt-2">
              <NextButton
                type="submit"
                solid
                blue
                :is-loading="uiFlags.isUpdating"
                :label="t('COMMUNICATION_CONNECTION.FORM.SAVE')"
              />
              <NextButton
                type="button"
                faded
                slate
                :is-loading="uiFlags.isTestingConnection"
                :disabled="isNew"
                :label="t('COMMUNICATION_CONNECTION.TEST_CONNECTION.BUTTON')"
                @click="runTestConnection"
              />
              <NextButton
                v-if="!isNew"
                type="button"
                faded
                ruby
                :label="t('COMMUNICATION_CONNECTION.DELETE.BUTTON')"
                @click="openDeleteDialog"
              />
            </div>
          </form>
        </CardLayout>

        <!-- Available WhatsApp Accounts (from Test Connection) -->
        <CardLayout v-if="testResult">
          <div class="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <span>
              {{ t('COMMUNICATION_CONNECTION.TEST_CONNECTION.REACHABLE') }}:
              {{ testResult.reachable ? '✓' : '✗' }}
            </span>
            <span>
              {{ t('COMMUNICATION_CONNECTION.TEST_CONNECTION.AUTHENTICATED') }}:
              {{ testResult.authenticated ? '✓' : '✗' }}
            </span>
          </div>
          <p v-if="testResult.error" class="text-n-ruby-11 text-sm mt-2">
            {{ testResult.error }}
          </p>

          <div class="mt-4">
            <h4 class="text-sm font-medium text-n-slate-12 mb-2">
              {{ t('COMMUNICATION_CONNECTION.TEST_CONNECTION.ACCOUNTS_TITLE') }}
            </h4>
            <p
              v-if="!testResult.accounts || testResult.accounts.length === 0"
              class="text-sm text-n-slate-11"
            >
              {{ t('COMMUNICATION_CONNECTION.TEST_CONNECTION.ACCOUNTS_EMPTY') }}
            </p>
            <table v-else class="w-full text-sm">
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th class="py-1 pr-4">
                    {{
                      t(
                        'COMMUNICATION_CONNECTION.TEST_CONNECTION.ACCOUNTS_TABLE.NAME'
                      )
                    }}
                  </th>
                  <th class="py-1 pr-4">
                    {{
                      t(
                        'COMMUNICATION_CONNECTION.TEST_CONNECTION.ACCOUNTS_TABLE.NUMBER'
                      )
                    }}
                  </th>
                  <th class="py-1 pr-4">
                    {{
                      t(
                        'COMMUNICATION_CONNECTION.TEST_CONNECTION.ACCOUNTS_TABLE.STATUS'
                      )
                    }}
                  </th>
                  <th class="py-1">
                    {{
                      t(
                        'COMMUNICATION_CONNECTION.TEST_CONNECTION.ACCOUNTS_TABLE.LAST_SEEN'
                      )
                    }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="account in testResult.accounts"
                  :key="account.gateway_account_id"
                  class="text-n-slate-12"
                >
                  <td class="py-1 pr-4">{{ account.gateway_instance_name }}</td>
                  <td class="py-1 pr-4">{{ account.phone_number || '—' }}</td>
                  <td class="py-1 pr-4">{{ account.connection_status }}</td>
                  <td class="py-1">
                    {{
                      account.last_seen_at
                        ? messageTimestamp(account.last_seen_at)
                        : '—'
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardLayout>
      </div>

      <Dialog
        ref="deleteDialogRef"
        type="alert"
        :title="t('COMMUNICATION_CONNECTION.DELETE.CONFIRM_TITLE')"
        :description="t('COMMUNICATION_CONNECTION.DELETE.CONFIRM_MESSAGE')"
        :is-loading="uiFlags.isDeleting"
        :confirm-button-label="t('COMMUNICATION_CONNECTION.DELETE.CONFIRM_YES')"
        :cancel-button-label="t('COMMUNICATION_CONNECTION.DELETE.CONFIRM_NO')"
        @confirm="removeConnection"
      />
    </template>
  </SettingsLayout>
</template>
